/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Device Context
********************************************************************************************
Capacitor device, network, and app state context

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       7th April 2025

*******************************************************************************************/
import React                            from 'react';
import { App }                          from '@capacitor/app';
import { Network }                      from '@capacitor/network';
import { Device }                       from '@capacitor/device';
import { Capacitor }                    from '@capacitor/core';
import { SafeArea }                     from 'capacitor-plugin-safe-area';

// Context
const DeviceContext = React.createContext(undefined);


// DEFAULT DATA
const PLATFORM_WEB                      = "web";
const PLATFORM_IOS                      = "ios";
const PLATFORM_ANDROID                  = "android";
const DEFAULT_INSET                     = 0;
const DEFAULT_CONNECTED                 = false;
const DEFAULT_CONNECTION_TYPE           = null;
const DEFAULT_IS_ACTIVE                 = false;
const DEFAULT_BATTERY_LEVEL             = 1;
const DEFAULT_BATTERY_CHARGING          = false;
const DEFAULT_PLATFORM                  = PLATFORM_WEB;
const DEFAULT_MODEL                     = "Unknown";
const DEFAULT_OS_VERSION                = "Unknown";
const DEFAULT_STATE_SAFE_AREA_INSET     = { top : DEFAULT_INSET, right: DEFAULT_INSET, bottom: DEFAULT_INSET, left: DEFAULT_INSET };
const DEFAULT_STATE_NETWORK_STATUS      = { connected : DEFAULT_CONNECTED, connectionType : DEFAULT_CONNECTION_TYPE };
const DEFAULT_STATE_APP_STATE           = { isActive : DEFAULT_IS_ACTIVE };
const DEFAULT_STATE_BATTERY_INFO        = { batteryLevel : DEFAULT_BATTERY_LEVEL, isCharging : DEFAULT_BATTERY_CHARGING };
const DEFAULT_STATE_DEVICE_INFO         = { platform: DEFAULT_PLATFORM, model: DEFAULT_MODEL, osVersion: DEFAULT_OS_VERSION };

// Provider
const DeviceProvider = ({ children }) => {

    const platform = Capacitor.getPlatform();

    const [deviceInfo,      setDeviceInfo]      = React.useState( DEFAULT_STATE_DEVICE_INFO );
    const [batteryInfo,     setBatteryInfo]     = React.useState( DEFAULT_STATE_BATTERY_INFO );
    const [appState,        setAppState]        = React.useState( DEFAULT_STATE_APP_STATE );
    const [networkStatus,   setNetworkStatus]   = React.useState( DEFAULT_STATE_NETWORK_STATUS);
    const [safeAreaInsets, setSafeAreaInsets]   = React.useState( DEFAULT_STATE_SAFE_AREA_INSET );


    // Memoized flag to indicate if it's a web app, ios app or android app.
    const [
        isWeb, 
        isIos, 
        isAndroid
    ]                   = React.useMemo(() => [PLATFORM_WEB, PLATFORM_IOS, PLATFORM_ANDROID].map(x => x === platform), [platform]);

    // Memoized flag to indicate if it's native (iOS or Android)
    const isNative      = React.useMemo(() => isIos || isAndroid, [isIos,isAndroid]);

    // Memoised isConnected
    const isConnected   = React.useMemo(() => Boolean(networkStatus?.connected), [networkStatus?.connected])

    // Memoised is touch screen
    const isTouchScreen = React.useMemo(() => 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0, []);

    // DEVICE INFO
    React.useEffect(() => {

        const updateDeviceInfo = async () => {
            try {
                const info = await Device.getInfo();
                setDeviceInfo(info || DEFAULT_STATE_DEVICE_INFO);
              } catch (err) {
                console.error('Error fetching device info:', err);
                setDeviceInfo(DEFAULT_STATE_DEVICE_INFO);
              }
        }

        updateDeviceInfo();
        // Cleanup
        return () => {

        }
    }, []);

    // BATTERY LEVEL
    const batteryListener = React.useRef(null)
    React.useEffect(() => {

        const handleChange = (data = {}) => {
            const { batteryLevel, isCharging } = { ...DEFAULT_STATE_BATTERY_INFO, ...data };
            setBatteryInfo(prev => ({
                ...prev,
                batteryLevel,
                isCharging
            }));
        };

        const updateBatteryInfo = async () => {
            try {
                const batteryInfo = await Device.getBatteryInfo();
                handleChange(batteryInfo);    
            } catch (err) {
                console.error("Error updating battery info", err);
            }
        }

        updateBatteryInfo()
        batteryListener.current = Device.addListener('batteryInfoChange', handleChange);
    
        return () => {
            if(batteryListener.current){
                if(batteryListener.current.remove === 'function')
                    batteryListener.current.remove();
                batteryListener.current = null;
            }
        }
    },[])

    // NETWORK
    const networkListener = React.useRef(null);
     React.useEffect(() => {

        const handleChange = (data = {}) => {
            const { connected, connectionType } = { ...DEFAULT_STATE_NETWORK_STATUS, ...data };
            setNetworkStatus(prev => ({
                ...prev,
                connected,
                connectionType
            }));
        };
    
        const process = async () => {
            try {
                const status = await Network.getStatus();
                handleChange(status);    
            } catch (err) {
                console.error('Error network status update', err);
            }
        }
        
        process();
        networkListener.current = Network.addListener('networkStatusChange', handleChange);
    
        return () => {
            if(networkListener.current){
                if(networkListener.current.remove === 'function')
                    networkListener.current.remove();
                networkListener.current = null;
            }
        }
    },[])
    

    // APP STATE
    const appStateListener = React.useRef(null);
    React.useEffect(() => {

        const handleChange = (data = {}) => {
            const { isActive } = { ...DEFAULT_STATE_APP_STATE, ...data };
            setAppState(prev => ({
                ...prev,
                isActive
            }));
        };

        const process = async () => {
            try {
                const appState = await App.getState();
                handleChange(appState);
            } catch (err) {
                console.error('Error processing state update', err);
            }
        }
    
        process();
        appStateListener.current = App.addListener('appStateChange', handleChange);
    
        return () => {
            if(appStateListener.current){
                if(appStateListener.current.remove === 'function')
                    appStateListener.current.remove();
                appStateListener.current = null;
            }
        }
    },[]);

    // SAFE AREA
    const safeAreaListener = React.useRef(null);
    React.useEffect(() => {
        const handleChange = (insets = {}) => {
            const { top, right, bottom, left } = { ...DEFAULT_STATE_SAFE_AREA_INSET, ...insets };
            setSafeAreaInsets(prev => ({...prev, top, right, bottom, left }));
        };
        const updateSafeAreaInsets = async () => {
            try {
                const { insets } = await SafeArea.getSafeAreaInsets();
                handleChange(insets);
            } catch (err) {
                console.error('Error fetching safe area insets:', err);
            }
        };
        updateSafeAreaInsets();
        safeAreaListener.current = SafeArea.addListener('safeAreaChanged', updateSafeAreaInsets);
        return () => {
            if(safeAreaListener.current){
                if(safeAreaListener.current.remove === 'function')
                    safeAreaListener.current.remove();
                safeAreaListener.current = null;
            }
        }

    },[]);

    // Inject Header Meta Tag
    React.useEffect(() => {

        if (!isNative)
            return;

        let meta, style;

        // Check and inject meta tag if not already present
        meta = document.querySelector('meta[name="viewport"][data-injected="true"]');
        if (!meta) {
            meta                = document.createElement('meta');
            meta.name           = "viewport";
            meta.content        = "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no";
            meta.setAttribute("data-injected", "true");
            document.head.appendChild(meta);
        }

        // Check and inject style tag if not already present
        style = document.querySelector('style[data-injected="ios-fix"]');
        if (!style) {
            style               = document.createElement('style');
            style.setAttribute("data-injected", "ios-fix");
            style.textContent   = `
                input, select, textarea {
                    font-size: 16px !important;
                }
            `;
            document.head.appendChild(style);
        }

        // Optional: don't remove if you want it persistent across app views
        return () => {
            if (meta?.parentNode) 
                document.head.removeChild(meta);
            if (style?.parentNode) 
                document.head.removeChild(style);
        };
    },[isNative]);

    // Memoize the context value to prevent unnecessary re-renders
    const value = {
        deviceInfo,
        batteryInfo,
        networkStatus,
        appState,
        safeAreaInsets,
        isConnected,
        isWeb,
        isIos,
        isAndroid,
        isNative,
        isTouchScreen
    };

    return (
        <DeviceContext.Provider value={value}>
            {children}
        </DeviceContext.Provider>
    );
};

// Consumer
const DeviceConsumer = ({ children }) => (
    <DeviceContext.Consumer>
        {context => {
            if (context === undefined) {
                throw new Error('DeviceConsumer must be used within a DeviceProvider');
            }
            return children(context);
        }}
    </DeviceContext.Consumer>
);

// Hook
const useDevice = () => {
    const context = React.useContext(DeviceContext);
    if (context === undefined) {
        throw new Error('useDevice must be used within a DeviceProvider');
    }
    return context;
};

// HOC to wrap components in DeviceProvider
const withDevice = (Component) => (props) => (
    <DeviceProvider>
        <Component {...props} />
    </DeviceProvider>
);

export {
    DeviceProvider,
    DeviceConsumer,
    useDevice,
    withDevice
}